import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useProjects, useAllItems, useFolders, useFolderMutations, useSaveProject, useSavePhase,
} from "@/hooks/useData";
import { pb } from "@/lib/pocketbase";
import { useQueryClient } from "@tanstack/react-query";
import { dueLabel, pct, shortDate } from "@/lib/format";
import { flattenTree, projectTree, type ProjectNode } from "@/lib/deps";
import { useIsMobile } from "@/lib/useMediaQuery";
import { PROJECT_PALETTE, PROJECT_STATUSES, PROJECT_STATUS_LABEL, type Project, type ProjectStatus } from "@/lib/types";
import { ProgressBar, card } from "@/components/atoms";
import { ProjectModal } from "@/components/ProjectModal";
import { useChrome } from "@/context/ChromeContext";
import { useToast } from "@/context/ToastContext";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { usePinnedProjects, useCategoryFilter, type CategoryFilter } from "@/lib/theme";
import { useUndoableDelete } from "@/hooks/useUndoableDelete";
import { statusEditable } from "@/lib/projectStatusSync";
import { feeLabel } from "@/lib/timeRollup";
import { openInTodoist, TODOIST_PROJECTS_URL } from "@/lib/todoistLinks";

type GroupBy = "stage" | "folder" | "client";

// Active and Archived, matching Todoist. The older stages (planning, hold,
// complete) count as Active.
const STAGES: { key: string; label: string; color: string; match: (s: ProjectStatus) => boolean }[] = [
  { key: "active", label: "Active", color: "var(--good)", match: (s) => s !== "archived" },
  { key: "archived", label: "Archived", color: "var(--muted3)", match: (s) => s === "archived" },
];

interface Group {
  key: string;
  label: string;
  color?: string;
  folderId?: string;   // set only when grouping by folder, and not for "No folder"
  entries: ProjectNode[];
  empty: string;
}

export function Projects() {
  const { data: projects = [] } = useProjects();
  const { data: items = [] } = useAllItems();
  const { data: folders = [] } = useFolders();
  const folderMut = useFolderMutations();
  const saveProject = useSaveProject();
  const undoableDelete = useUndoableDelete();
  const [pins, togglePin] = usePinnedProjects();
  const savePhase = useSavePhase();
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();
  const mobile = useIsMobile();

  const [groupBy, setGroupBy] = useState<GroupBy>("stage");
  const [category, setCategory] = useCategoryFilter();
  const [editing, setEditing] = useState<Project | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [newFolder, setNewFolder] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Project | null>(null);

  // Work/personal is orthogonal to grouping, so it filters the input rather
  // than adding a fourth way to group. Projects synced from Todoist default to
  // "work"; anything with no category set stays visible under either filter so
  // a project can never vanish just because it predates the field.
  const visible = projects.filter((p) => {
    if (p.kind === "inbox") return false;
    if (category === "all" || !p.category) return true;
    return p.category === category;
  });
  const tree = projectTree(visible);

  const countsOf = useMemo(() => {
    const projectOf = (i: any) => i?.expand?.phase?.expand?.project?.id ?? i?.expand?.phase?.project;
    const acc = new Map<string, { done: number; total: number }>();
    for (const i of items) {
      const id = projectOf(i);
      if (!id) continue;
      const c = acc.get(id) || { done: 0, total: 0 };
      c.total += 1;
      if (i.status === "done") c.done += 1;
      acc.set(id, c);
    }
    return acc;
  }, [items]);

  const groups: Group[] = useMemo(() => {
    if (groupBy === "stage") {
      return STAGES.map((st) => ({
        key: st.key,
        label: st.label,
        color: st.color,
        entries: tree.filter((e) => st.match(e.project.status)),
        empty: `Nothing ${st.label.toLowerCase()}.`,
      }));
    }
    if (groupBy === "folder") {
      const known = new Set(folders.map((f) => f.id));
      const out: Group[] = folders.map((f) => ({
        key: f.id,
        label: f.name,
        color: f.color,
        folderId: f.id,
        entries: tree.filter((e) => e.project.folderId === f.id),
        empty: "No projects in this folder.",
      }));
      out.push({
        key: "__none",
        label: "No folder",
        entries: tree.filter((e) => !e.project.folderId || !known.has(e.project.folderId)),
        empty: "Everything is filed.",
      });
      return out;
    }
    const clients = [...new Set(tree.map((e) => e.project.client).filter(Boolean))].sort();
    const out: Group[] = clients.map((c) => ({
      key: c,
      label: c,
      entries: tree.filter((e) => e.project.client === c),
      empty: "",
    }));
    const none = tree.filter((e) => !e.project.client);
    if (none.length) out.push({ key: "__noclient", label: "No client", entries: none, empty: "" });
    return out;
  }, [groupBy, tree, folders]);

  // ---- Creating ------------------------------------------------------------

  /**
   * Projects come from Todoist. One created here would have no todoistId, so
   * the sync would ignore it forever and it would never exist in the app you
   * actually work in — an orphan that looks real. So the button opens Todoist.
   */
  function createInTodoist() {
    openInTodoist(TODOIST_PROJECTS_URL);
    toast("New projects appear here on the next sync");
  }


  /** Overview › Definition, in edit mode (§10). */
  function openDefinition(id: string) {
    nav(`/project/${id}`, { state: { openDefinition: true } });
  }

  // ---- Folder rename / delete ----------------------------------------------

  function commitRename(folderId: string, value: string) {
    const name = value.trim();
    setRenaming(null);
    const current = folders.find((f) => f.id === folderId);
    if (!name || !current || name === current.name) return;
    folderMut.update.mutate({ id: folderId, name }, { onSuccess: () => toast(`Folder renamed to “${name}”`) });
  }

  function deleteFolder(folderId: string, label: string, moved: number) {
    folderMut.remove.mutate(folderId, {
      onSuccess: () =>
        toast(
          moved === 0
            ? `Deleted “${label}”`
            : `Deleted “${label}” — ${moved} project${moved === 1 ? "" : "s"} moved to No folder`
        ),
    });
  }

  useChrome(
    {
      title: "Projects",
      subtitle: `${visible.length} project${visible.length === 1 ? "" : "s"} · ${items.filter((i) => i.status !== "done").length} open items`,
      actions: <button onClick={() => nav("/import")} style={secondaryBtn}>Import</button>,
    },
    [visible.length, items.length]
  );

  return (
    <div style={{ padding: mobile ? "16px 16px 90px" : "20px 36px 90px", maxWidth: 1180 }}>
      {/* Header row (§10) */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ display: "flex", padding: 3, background: "var(--tint)", borderRadius: 999 }}>
          {([["stage", "By status"], ["folder", "By folder"], ["client", "By client"]] as [GroupBy, string][]).map(([k, label]) => {
            const on = k === groupBy;
            return (
              <button key={k} onClick={() => setGroupBy(k)} style={{
                padding: "6px 14px", borderRadius: 999, fontSize: 12.5, cursor: "pointer", border: "none",
                background: on ? "var(--surface)" : "transparent",
                color: on ? "var(--accent)" : "var(--ink3)",
                fontWeight: on ? "var(--fw-title)" : "var(--fw-medium)",
                boxShadow: on ? "var(--shadowCard)" : "none",
              }}>{label}</button>
            );
          })}
        </div>

        <div style={{ display: "flex", padding: 3, background: "var(--tint)", borderRadius: 999 }}>
          {([["all", "All"], ["work", "Work"], ["personal", "Personal"]] as [CategoryFilter, string][]).map(([k, label]) => {
            const on = k === category;
            return (
              <button key={k} onClick={() => setCategory(k)} style={{
                padding: "6px 14px", borderRadius: 999, fontSize: 12.5, cursor: "pointer", border: "none",
                background: on ? "var(--surface)" : "transparent",
                color: on ? "var(--accent)" : "var(--ink3)",
                fontWeight: on ? "var(--fw-title)" : "var(--fw-medium)",
                boxShadow: on ? "var(--shadowCard)" : "none",
              }}>{label}</button>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 9, marginLeft: "auto", alignItems: "center" }}>
          {newFolder ? (
            <input
              autoFocus
              placeholder="Folder name"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const name = e.currentTarget.value.trim();
                  setNewFolder(false);
                  if (name) folderMut.add.mutate(
                    { name, color: PROJECT_PALETTE[folders.length % PROJECT_PALETTE.length], position: folders.length },
                    { onSuccess: () => { setGroupBy("folder"); toast(`Folder “${name}” added`); } }
                  );
                }
                if (e.key === "Escape") setNewFolder(false);
              }}
              onBlur={() => setNewFolder(false)}
              style={{ padding: "7px 11px", borderRadius: 999, border: "1px solid var(--accentLine)", background: "var(--surface)", fontSize: 12.5, color: "var(--ink)", width: 170 }}
            />
          ) : (
            <button onClick={() => setNewFolder(true)} style={secondaryBtn}>+ Folder</button>
          )}

          <button onClick={createInTodoist} title="Projects are created in Todoist" style={primaryBtn}>+ Project in Todoist ↗</button>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
        {groups.map((g) => {
          const isRenaming = renaming === g.folderId;
          return (
            <div key={g.key}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, padding: "0 2px", minHeight: 26 }}>
                {g.color
                  ? <span style={{ width: 9, height: 9, borderRadius: "50%", background: g.color, flex: "none" }} />
                  : <span style={{ width: 9, height: 9, borderRadius: "50%", border: "1.5px dashed var(--muted3)", flex: "none" }} />}

                {isRenaming ? (
                  <RenameField initial={g.label} onCommit={(v) => commitRename(g.folderId!, v)} />
                ) : (
                  <span style={{ fontSize: 13, fontWeight: "var(--fw-medium)", letterSpacing: "-0.01em" }}>{g.label}</span>
                )}

                <span className="mono" style={{ fontSize: 11, color: "var(--muted2)" }}>{g.entries.length}</span>

                {g.folderId && !isRenaming && (
                  <span style={{ display: "flex", gap: 10, marginLeft: 6, alignItems: "center" }}>
                    <span style={{ display: "flex", gap: 3 }}>
                      {PROJECT_PALETTE.map((c) => (
                        <button
                          key={c}
                          onClick={() => folderMut.update.mutate({ id: g.folderId!, color: c })}
                          aria-label={`Colour ${c}`}
                          style={{
                            width: 11, height: 11, borderRadius: 3, background: c, cursor: "pointer",
                            border: g.color === c ? "1.5px solid var(--ink)" : "1.5px solid transparent",
                          }}
                        />
                      ))}
                    </span>
                    {folders.length > 1 && (() => {
                      const fi = folders.findIndex((f) => f.id === g.folderId);
                      const move = (d: number) => {
                        const next = [...folders];
                        [next[fi], next[fi + d]] = [next[fi + d], next[fi]];
                        folderMut.reorder.mutate(next);
                      };
                      return (
                        <span style={{ display: "flex", gap: 2 }}>
                          <button onClick={() => move(-1)} disabled={fi <= 0} aria-label={`Move ${g.label} up`} title="Move up"
                            style={{ ...linkBtn, opacity: fi <= 0 ? 0.35 : 1 }}>↑</button>
                          <button onClick={() => move(1)} disabled={fi >= folders.length - 1} aria-label={`Move ${g.label} down`} title="Move down"
                            style={{ ...linkBtn, opacity: fi >= folders.length - 1 ? 0.35 : 1 }}>↓</button>
                        </span>
                      );
                    })()}
                    <button onClick={() => setRenaming(g.folderId!)} style={linkBtn}>Rename</button>
                    <button onClick={() => deleteFolder(g.folderId!, g.label, projects.filter((x) => x.folderId === g.folderId).length)} style={{ ...linkBtn, color: "var(--muted)" }}>Delete</button>
                  </span>
                )}
              </div>

              {g.entries.length === 0 ? (
                g.empty ? (
                  <div style={{ border: "1px dashed var(--line2)", borderRadius: 14, padding: 18, fontSize: 12.5, color: "var(--muted2)" }}>
                    {g.empty}
                  </div>
                ) : null
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {g.entries.map((e) => (
                    <div key={e.project.id} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <ProjectRow
                        p={e.project}
                        counts={countsOf.get(e.project.id) || { done: 0, total: 0 }}
                        folders={folders}
                        pinned={pins.includes(e.project.id)}
                        onPin={() => togglePin(e.project.id)}
                        onDelete={() => {
                          if (e.children.length) { toast("Move or delete its sub-projects first"); return; }
                          setConfirmDelete(e.project);
                        }}
                        onOpen={() => nav(`/project/${e.project.id}`)}
                        onPatch={(patch) => saveProject.mutate({ id: e.project.id, ...patch })}
                      />
                      {flattenTree(e.children, 1).map(({ project: c, depth, hasChildren }) => (
                        <ProjectRow
                          key={c.id}
                          p={c}
                          sub
                          depth={depth}
                          counts={countsOf.get(c.id) || { done: 0, total: 0 }}
                          folders={folders}
                          pinned={pins.includes(c.id)}
                          onPin={() => togglePin(c.id)}
                          onDelete={() => {
                            if (hasChildren) { toast("Move or delete its sub-projects first"); return; }
                            setConfirmDelete(c);
                          }}
                          onOpen={() => nav(`/project/${c.id}`)}
                          onPatch={(patch) => saveProject.mutate({ id: c.id, ...patch })}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title="Delete project?"
          message={`“${confirmDelete.name}” and its ${countsOf.get(confirmDelete.id)?.total ?? 0} item(s) will be permanently deleted, along with every phase, note and file on it. This can’t be undone.`}
          confirmLabel="Delete project"
          onConfirm={() => {
            undoableDelete({
              collection: "projects",
              id: confirmDelete.id,
              queryKeys: [["projects"], ["phases"], ["items"]],
              message: `Deleted “${confirmDelete.name}”`,
            });
            setConfirmDelete(null);
          }}
          onClose={() => setConfirmDelete(null)}
        />
      )}

      {editing && <ProjectModal project={editing} allProjects={projects} onClose={() => setEditing(null)} />}
    </div>
  );
}

/** Inline rename — commits on Enter or blur, never a browser prompt (§10). */
function RenameField({ initial, onCommit }: { initial: string; onCommit: (v: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const settled = useRef(false);
  const commit = (v: string) => { if (settled.current) return; settled.current = true; onCommit(v); };
  return (
    <input
      ref={ref}
      defaultValue={initial}
      autoFocus
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); commit(e.currentTarget.value); }
        if (e.key === "Escape") { e.preventDefault(); commit(initial); }
      }}
      onBlur={(e) => commit(e.target.value)}
      style={{
        padding: "3px 8px", borderRadius: 7, border: "1px solid var(--accentLine)",
        background: "var(--surface)", fontSize: 13, color: "var(--ink)", width: 200,
      }}
    />
  );
}

function ProjectRow({ p, counts, folders, sub, depth = 0, pinned, onPin, onDelete, onOpen, onPatch }: {
  p: Project;
  counts: { done: number; total: number };
  folders: { id: string; name: string }[];
  sub?: boolean;
  /** How deep a sub-project sits: 1 under a top-level project, 2 under that… */
  depth?: number;
  pinned?: boolean;
  onPin?: () => void;
  onDelete?: () => void;
  onOpen: () => void;
  onPatch: (patch: Partial<Project>) => void;
}) {
  const percent = pct(counts.done, counts.total);
  const due = dueLabel(p.due, "");
  const editable = statusEditable(p);
  const meta = [p.client, `${counts.total} item${counts.total === 1 ? "" : "s"}`, feeLabel(p)].filter(Boolean).join(" · ");

  /**
   * The whole row opens the project, not just its name.
   *
   * The name was the only target in a 14px-padded row carrying a progress bar,
   * a date and three controls — so a click anywhere in the two-thirds of the
   * row that is not text did nothing. The controls below stop the click so
   * changing a folder or a status still does what it says.
   */
  return (
    <div
      className="row-hover"
      onClick={onOpen}
      style={{ ...card, borderRadius: 14, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12, marginLeft: sub ? 26 * Math.max(1, depth) : 0, cursor: "pointer" }}
    >
      <span style={{ width: 12, height: 12, borderRadius: 4, background: p.color, flex: "none" }} />

      <button onClick={onOpen} style={{ flex: 1, minWidth: 0, textAlign: "left", border: "none", background: "none", cursor: "pointer", padding: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: "var(--fw-title)", letterSpacing: "-0.015em", color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {sub && <span style={{ color: "var(--muted3)", marginRight: 5 }}>↳</span>}
          {p.name}
        </div>
        {meta && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{meta}</div>}
      </button>

      <span style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
        <span style={{ width: 74 }}><ProgressBar value={percent} color={p.color} /></span>
        <span className="mono" style={{ fontSize: 11.5, color: "var(--muted2)", width: 30, textAlign: "right" }}>{percent}%</span>
      </span>

      <span className="mono" style={{ flex: "none", width: 54, textAlign: "right", fontSize: 11.5, color: due.overdue ? "var(--bad)" : "var(--muted2)" }}>
        {p.due ? shortDate(p.due) : "—"}
      </span>

      {/* A sub-project lives wherever its parent does, so it has no folder of
          its own to pick. Offering one here is how sub-projects used to end up
          with a parent AND a folder — and then lose the parent on their next
          save in the edit dialog. */}
      {sub ? (
        <span title="Sub-projects sit under their parent" style={{ ...rowSelect, color: "var(--muted3)", cursor: "default", display: "inline-flex", alignItems: "center" }}>
          Under parent
        </span>
      ) : (
        <select
          value={p.folderId || ""}
          onChange={(e) => onPatch({ folderId: e.target.value || null } as Partial<Project>)}
          onClick={(e) => e.stopPropagation()}
          title="Folder"
          style={rowSelect}
        >
          <option value="">No folder</option>
          {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      )}

      <select
        value={p.status === "archived" ? "archived" : "active"}
        onChange={(e) => onPatch({ status: e.target.value as ProjectStatus })}
        onClick={(e) => e.stopPropagation()}
        disabled={!editable}
        title={editable ? "Status" : `Follows Todoist: ${p.status === "archived" ? "unarchive" : "archive"} it there to change this`}
        style={rowSelect}
      >
        {PROJECT_STATUSES.map((s) => <option key={s} value={s}>{PROJECT_STATUS_LABEL[s]}</option>)}
      </select>

      <button
        onClick={(e) => { e.stopPropagation(); onPin?.(); }}
        title={pinned ? "Unpin from the sidebar" : "Pin to the sidebar"}
        aria-pressed={!!pinned}
        style={{ ...iconBtn, color: pinned ? "var(--accent)" : "var(--muted3)" }}
      >
        {pinned ? "★" : "☆"}
      </button>

      <button onClick={(e) => { e.stopPropagation(); onDelete?.(); }} title={`Delete ${p.name}`} style={{ ...iconBtn, color: "var(--muted3)" }}>✕</button>
    </div>
  );
}

const iconBtn: React.CSSProperties = {
  flex: "none", width: 26, height: 26, borderRadius: 999, border: "none",
  background: "none", cursor: "pointer", fontSize: 13, lineHeight: 1, padding: 0,
};
const rowSelect: React.CSSProperties = {
  flex: "none", padding: "5px 8px", borderRadius: 999, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink3)", fontSize: 11.5, cursor: "pointer", maxWidth: 130,
};
const primaryBtn: React.CSSProperties = { padding: "8px 14px", borderRadius: 999, border: "none", background: "var(--accent)", color: "var(--onAccent)", fontSize: 12.5, fontWeight: 500, cursor: "pointer" };
const secondaryBtn: React.CSSProperties = { padding: "8px 14px", borderRadius: 999, border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)", fontSize: 12.5, cursor: "pointer" };
const linkBtn: React.CSSProperties = { background: "none", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: 11.5, padding: 0 };
